import { useState } from "react";
import Loader from "../Loader";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getLogged } from "../../redux/userSlice";
import { resetBacklink } from "../../redux/backlinkSlice";
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';

// Classi condivise per gli input restylati (override delle regole globali in index.css)
const INPUT_BASE = "w-full !py-3.5 !pl-12 !rounded-xl !border !border-neutral-200 bg-neutral-50 placeholder:text-neutral-400 focus:!border-[#225424] focus:bg-white text-base outline-none";

const LoginForm = ({ setSection }) => {
    const { t } = useTranslation();
    const [inputData, setInputData] = useState({
        email: '',
        password: ''
    })
    const handleInputData = (event) => {
        const { id, value } = event.target;
        setInputData(prevState => ({
            ...prevState,
            [id]: value
        })
        )
    }
    const [fetchStatus, setFetchStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [isShowPassword, setIsShowPassword] = useState(false);

    // Send login fetch
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const backlink = useSelector(state => state.backlink.link);
    const sendLoginData = async () => {
        try {
            setFetchStatus('loading');
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/login`;
            const headers = { 'Content-Type': 'application/json' };
            const options = { method: 'POST', headers, body: JSON.stringify(inputData) }
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setFetchStatus('succeeded');
                localStorage.setItem('ticino-wild-token', result.payload.token);
                dispatch(getLogged(result.payload));
                const destination = backlink || "/";
                dispatch(resetBacklink());
                setTimeout(() => {
                    navigate(destination, { replace: true });
                }, 3000);
            } else {
                const error = await response.json();
                console.log('LoginForm error: ', error);
                setFetchStatus('failed');
                setError(error.message);
            }
        } catch (error) {
            setFetchStatus('failed');
            setError(error.message);
        }
    }

    // Handle retry login
    const handleRetryLogin = () => {
        setFetchStatus('idle');
        setError(null);
        setInputData({
            email: '',
            password: ''
        })
        setIsShowPassword(false);
    }

    const isFormValid = inputData.email && inputData.password;

    return (
        <div className="w-full flex flex-col">
            {fetchStatus === 'idle' && (
                <>
                    {/* Mobile: titolo unico stilato come il sottotitolo desktop per guadagnare spazio */}
                    <h2 className="md:hidden !text-base !font-semibold text-[#225424] text-center mb-6 leading-relaxed">
                        {t('RegisterLoginPage.LoginForm.mobileTitle')}
                    </h2>
                    {/* Desktop: titolo + sottotitolo */}
                    <h2 className="hidden md:block !text-3xl !font-bold text-[#225424] text-center">
                        {t('RegisterLoginPage.LoginForm.welcomeTitle')}
                    </h2>
                    <p className="hidden md:block mt-2 mb-6 text-sm text-neutral-600 text-center leading-relaxed">
                        {t('RegisterLoginPage.LoginForm.welcomeSubtitle')}
                    </p>

                    {/* Email */}
                    <div className="relative mb-3">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#b05704] pointer-events-none" />
                        <input
                            type="email"
                            id="email"
                            autoComplete="email"
                            inputMode="email"
                            placeholder={t('RegisterLoginPage.LoginForm.emailPlaceholder')}
                            className={`${INPUT_BASE} !pr-4`}
                            value={inputData.email}
                            onChange={handleInputData}
                        />
                    </div>

                    {/* Password */}
                    <div className="relative mb-2">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#b05704] pointer-events-none" />
                        <input
                            type={isShowPassword ? "text" : "password"}
                            id="password"
                            autoComplete="current-password"
                            placeholder={t('RegisterLoginPage.LoginForm.passwordPlaceholder')}
                            className={`${INPUT_BASE} !pr-12`}
                            value={inputData.password}
                            onChange={handleInputData}
                        />
                        <button
                            type="button"
                            aria-label={isShowPassword ? 'hide password' : 'show password'}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-500"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setIsShowPassword(p => !p);
                            }}
                        >
                            {isShowPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>

                    <div className="flex justify-end mb-5">
                        <Link to="/reset-password" className="text-xs text-[#b05704] underline">
                            {t('RegisterLoginPage.LoginForm.forgotPassword')}
                        </Link>
                    </div>

                    <button
                        type="button"
                        onClick={sendLoginData}
                        disabled={!isFormValid}
                        className={`w-full py-3.5 rounded-xl font-semibold text-base transition ${isFormValid
                            ? 'bg-[#225424] text-white hover:bg-[#1c4a1e] active:bg-[#1c4a1e]'
                            : 'bg-neutral-200 text-neutral-400 cursor-default'
                            }`}
                    >
                        {t('RegisterLoginPage.LoginForm.loginButton')}
                    </button>

                    <div className="flex items-center my-5">
                        <div className="flex-1 h-px bg-neutral-200" />
                        <span className="mx-3 text-xs text-neutral-500">
                            {t('RegisterLoginPage.LoginForm.notHaveAccount')}
                        </span>
                        <div className="flex-1 h-px bg-neutral-200" />
                    </div>

                    <button
                        type="button"
                        onClick={() => setSection('Register')}
                        className="w-full py-3.5 rounded-xl font-semibold text-base bg-[#b05704] text-white hover:bg-[#964804] active:bg-[#964804] transition"
                    >
                        {t('RegisterLoginPage.LoginForm.registerCta')}
                    </button>
                </>
            )}

            {fetchStatus === 'loading' && (
                <div className="py-12 flex justify-center"><Loader /></div>
            )}

            {fetchStatus === 'succeeded' && (
                <h3 className="!text-xl text-center py-12 text-[#225424]">
                    {t('RegisterLoginPage.LoginForm.loginSucceeded')}
                </h3>
            )}

            {fetchStatus === 'failed' && error && (
                <div className="flex flex-col items-center py-6 gap-2">
                    <h3 className="!text-xl text-[#b05704] text-center">
                        {t('RegisterLoginPage.LoginForm.loginFailed')}
                    </h3>
                    <p className="text-sm text-neutral-700 text-center">{error}</p>
                </div>
            )}

            {error && (
                <button
                    type="button"
                    onClick={handleRetryLogin}
                    className="mt-4 mx-auto underline text-sm text-[#225424]"
                >
                    {t('RegisterLoginPage.LoginForm.retry')}
                </button>
            )}
        </div>
    )
}

export default LoginForm;
